import { getIdToken } from './firebase';

const API_BASE = import.meta.env.VITE_API_BASE || 'https://api.linkr.fyi';

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getIdToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

export async function getJSON<T>(path: string): Promise<T> {
  const r = await authedFetch(path);
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  return r.json();
}

export async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const r = await authedFetch(path, { method: 'POST', body: JSON.stringify(body) });
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  return r.json();
}

export async function putJSON<T>(path: string, body: unknown): Promise<T> {
  const r = await authedFetch(path, { method: 'PUT', body: JSON.stringify(body) });
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  return r.json();
}

export async function patchJSON<T>(path: string, body: unknown): Promise<T> {
  const r = await authedFetch(path, { method: 'PATCH', body: JSON.stringify(body) });
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  return r.json();
}

export async function del(path: string): Promise<void> {
  const r = await authedFetch(path, { method: 'DELETE' });
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
}

export interface MeResponse {
  user_id: string;
  email: string;
  name: string;
  is_admin: boolean;
  plan?: string;
  codeLimit?: number;
  codesUsed?: number;
  prepaidUntil?: string | null;
}

export interface Link {
  id: string;
  user_id: string;
  type: string;
  payload: Record<string, unknown>;
  destination: string;
  active: boolean;
  branded?: boolean;
  qrImageUrl?: string;
  prepaidUntil?: string | null;
  scanCount?: number;
  created_at: string;
  updated_at: string;
}

export const fetchMe = () => getJSON<MeResponse>('/auth/me');
export const fetchMyLinks = () => getJSON<{ data: Link[] }>('/buckets/mine/links');
export const fetchAllLinks = () => getJSON<{ data: Link[] }>('/buckets/all/links');
export const fetchLink = (id: string, all = false) =>
  getJSON<{ data: Link }>(all ? `/buckets/all/links/${id}` : `/buckets/mine/links/${id}`);
export const updateLink = (id: string, body: Partial<Link>, all = false) =>
  putJSON<{ data: Link }>(all ? `/buckets/all/links/${id}` : `/buckets/mine/links/${id}`, body);
export const deleteLink = (id: string, all = false) =>
  del(all ? `/buckets/all/links/${id}` : `/buckets/mine/links/${id}`);
export const publishLink = (linkId: string, destination?: string, opts: { asFree?: boolean } = {}) =>
  postJSON<{ status: string; branded: boolean }>('/actions/publish-link', {
    linkId,
    destination,
    asFree: opts.asFree ?? false,
  });

export interface AdminUser {
  id: string;
  email?: string;
  plan?: string;
  codeLimit?: number;
  codesUsed?: number;
  prepaidUntil?: string | null;
  subscriptionId?: string;
  createdAt?: string;
}

export const fetchAdminUsers = () => getJSON<{ data: AdminUser[] }>('/admin/users');
export const patchAdminUser = (uid: string, body: { plan?: string; prepaidUntil?: string | null }) =>
  patchJSON<{ data: AdminUser }>(`/admin/users/${uid}`, body);

const SLUG_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
function randomSlug(len = 6) {
  const buf = new Uint8Array(len);
  crypto.getRandomValues(buf);
  let s = '';
  for (let i = 0; i < len; i++) s += SLUG_ALPHABET[buf[i] % SLUG_ALPHABET.length];
  return s;
}

export async function createLink(input: { type: string; payload: Record<string, unknown>; destination: string }): Promise<Link> {
  for (let attempt = 0; attempt < 5; attempt++) {
    const id = randomSlug();
    const r = await authedFetch('/buckets/mine/links', {
      method: 'POST',
      body: JSON.stringify({ id, ...input, active: false, scanCount: 0 }),
    });
    if (r.status === 409) continue;
    if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
    const body = (await r.json()) as { data: Link };
    return body.data;
  }
  throw new Error('Could not allocate slug after 5 attempts');
}
